import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import config from '../../config';
import LinkedCallLogs from '../../components/LinkedCallLogs';
import VehicleDetailsModal from './VehicleDetailsModal';
import './VehicleDetails.css';

const TABS = [
  { key: 'new', label: 'New' },
  { key: 'due', label: 'Due today' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'follow_up', label: 'Follow up' },
  { key: 'onboarded', label: 'Onboarded' },
  { key: 'dropped', label: 'Dropped' },
  { key: 'all', label: 'All leads' },
];

const STATUS_STYLE = {
  new: { background: '#dbeafe', color: '#1e40af' },
  contacted: { background: '#fef3c7', color: '#92400e' },
  follow_up: { background: '#ede9fe', color: '#6d28d9' },
  onboarded: { background: '#d1fae5', color: '#065f46' },
  dropped: { background: '#fee2e2', color: '#991b1b' },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('id_token') || ''}`,
});

const imageMessageUrl = (text) => {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const body = raw.toLowerCase().startsWith('photo:') ? raw.slice(6).trim() : raw;
  if (!/^https?:\/\//i.test(body)) return null;
  const path = body.split('?')[0].toLowerCase();
  if (raw.toLowerCase().startsWith('photo:') || /\.(jpe?g|png|gif|webp|bmp|heic)$/.test(path)) {
    return body;
  }
  return null;
};

const LeadMessageBody = ({ content }) => {
  const found = imageMessageUrl(content);
  if (!found) return content || '—';
  const needsProxy = /(^|\.)facebook\.com\b|(^|\.)fbsbx\.com\b|(^|\.)fbcdn\.net\b|(^|\.)whatsapp\.net\b/i.test(found);
  const photoUrl = needsProxy
    ? `${config.opsApiBase}/media-proxy?url=${encodeURIComponent(found)}`
    : found;
  return (
    <div>
      <img
        src={photoUrl}
        alt="Driver photo"
        style={{
          maxWidth: '100%',
          maxHeight: 240,
          borderRadius: 8,
          display: 'block',
          objectFit: 'contain',
          backgroundColor: 'rgba(255,255,255,0.5)',
        }}
      />
      <a
        href={photoUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#1d4ed8',
          textDecoration: 'none',
          fontSize: 12,
          marginTop: 8,
          display: 'inline-block',
          fontWeight: 600,
        }}
      >
        Open full image
      </a>
    </div>
  );
};

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const VehicleLeads = () => {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [contactByFrom, setContactByFrom] = useState('');
  const [contactByTo, setContactByTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [onboardMenuLeadId, setOnboardMenuLeadId] = useState(null);
  const [vehicleMenuLeadId, setVehicleMenuLeadId] = useState(null);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const pageSize = 50;

  const openOnboardedVehicle = (vehicleId) => {
    const id = Number(vehicleId);
    if (!id) return;
    setSelectedVehicleId(id);
    setVehicleModalOpen(true);
  };

  const loadCounts = useCallback(async () => {
    try {
      const response = await fetch(`${config.opsApiBase}/vehicle-leads/counts`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (response.ok) setCounts(data.counts || {});
    } catch {
      /* ignore badge errors */
    }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (activeTab === 'due') params.set('view', 'due');
      else if (activeTab !== 'all') params.set('status', activeTab);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());
      if (contactByFrom) params.set('contact_by_from', contactByFrom);
      if (contactByTo) params.set('contact_by_to', contactByTo);

      const response = await fetch(`${config.opsApiBase}/vehicle-leads?${params.toString()}`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to load vehicle leads');
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
    } catch (err) {
      setError(err.message || 'Failed to load vehicle leads');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, searchTerm, contactByFrom, contactByTo]);

  useEffect(() => {
    loadLeads();
    loadCounts();
  }, [loadLeads, loadCounts]);

  // Auto-refresh list every 10s so new WhatsApp leads show up without manual refresh.
  useEffect(() => {
    const timer = setInterval(() => {
      loadLeads();
      loadCounts();
    }, 10000);
    return () => clearInterval(timer);
  }, [loadLeads, loadCounts]);

  const patchLead = async (leadId, body) => {
    setSavingId(leadId);
    try {
      const response = await fetch(`${config.opsApiBase}/vehicle-leads/${leadId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Failed to update lead');
      }
      setItems((prev) =>
        prev.map((row) => (row.id === leadId ? { ...row, ...data.record } : row)),
      );
      loadCounts();
      return data.record;
    } catch (err) {
      setError(err.message || 'Failed to update lead');
      return null;
    } finally {
      setSavingId(null);
    }
  };

  const goOnboard = (row, type = 'normal') => {
    setOnboardMenuLeadId(null);
    // Do NOT mark onboarded here — same as register-vehicle: onboarded only
    // after a real vehicle is created (shows as Onboarded (V-123)).
    const params = new URLSearchParams();
    if (row.phone_number) params.set('phone', row.phone_number);
    if (row.driver_name) params.set('name', row.driver_name);
    params.set('vehicleLeadId', String(row.id));
    if (type === 'transporter') params.set('type', 'transporter');
    history.push(`/app/drivers/new?${params.toString()}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="vehicle-details-container" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            Vehicle Leads
            <span style={{
              fontSize: 13, fontWeight: 600, background: '#e2e8f0', color: '#334155',
              borderRadius: 999, padding: '2px 10px',
            }}>
              {counts.all || total || 0}
            </span>
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>
            Capture first — onboard manually when ready. No auto driver create.
          </p>
        </div>
        <button type="button" onClick={() => { loadLeads(); loadCounts(); }} style={pagerBtnStyle}>
          Refresh
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {TABS.map((tab) => {
          const count = counts[tab.key === 'all' ? 'all' : tab.key];
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => { setPage(1); setActiveTab(tab.key); }}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: active ? '1px solid #2563eb' : '1px solid #cbd5e1',
                background: active ? '#2563eb' : '#fff',
                color: active ? '#fff' : '#0f172a',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {tab.label}{typeof count === 'number' ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginBottom: 12 }}>
        <input
          value={searchTerm}
          onChange={(e) => { setPage(1); setSearchTerm(e.target.value); }}
          placeholder="Search by phone, name, from, to, or vehicle..."
          style={{
            flex: '1 1 280px',
            maxWidth: 480,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
          }}
        />
        <div>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: '#64748b',
            marginBottom: 4,
          }}>
            Contact by
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            background: '#fff',
          }}>
            <input
              type="date"
              value={contactByFrom}
              onChange={(e) => { setPage(1); setContactByFrom(e.target.value); }}
              aria-label="Contact by from"
              style={{ border: 'none', outline: 'none', fontSize: 13 }}
            />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>to</span>
            <input
              type="date"
              value={contactByTo}
              onChange={(e) => { setPage(1); setContactByTo(e.target.value); }}
              aria-label="Contact by to"
              style={{ border: 'none', outline: 'none', fontSize: 13 }}
            />
          </div>
        </div>
        {(contactByFrom || contactByTo) ? (
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setContactByFrom('');
              setContactByTo('');
            }}
            style={pagerBtnStyle}
          >
            Clear dates
          </button>
        ) : null}
      </div>

      {error ? (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={thStyle}>Phone / Name</th>
              <th style={thStyle}>Details</th>
              <th style={thStyle}>Vehicle</th>
              <th style={thStyle}>Contact by</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
                  Loading vehicle leads...
                </td>
              </tr>
            ) : null}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
                  No vehicle leads in this tab.
                </td>
              </tr>
            ) : null}
            {items.map((row) => {
              const status = String(row.status || 'new').toLowerCase();
              const badge = STATUS_STYLE[status] || STATUS_STYLE.new;
              const messages = Array.isArray(row.messages) ? row.messages : [];
              const notes = Array.isArray(row.notes) ? row.notes : [];
              const isOpen = expandedId === row.id;
              const busy = savingId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={() => setExpandedId(isOpen ? null : row.id)}
                    style={{ borderTop: '1px solid #e2e8f0', cursor: 'pointer' }}
                  >
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>#{row.id}</div>
                      <div>{row.phone_number || '—'}</div>
                      <div style={{ color: '#64748b', fontSize: 13 }}>{row.driver_name || '—'}</div>
                      <span style={{ ...pagerBtnStyle, marginTop: 6, fontSize: 12, display: 'inline-block' }}>
                        {messages.length} msgs {isOpen ? '▾' : '▸'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div>
                        {(row.from_location || '—')} → {(row.to_location || '—')}
                      </div>
                      <div style={{ color: '#64748b', fontSize: 13 }}>
                        Date: {row.trip_date || '—'} · Lang: {row.language || '—'}
                      </div>
                      {row.details ? (
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{row.details}</div>
                      ) : null}
                      {row.already_onboarded || row.driver_id ? (
                        <div style={{ fontSize: 12, color: '#0f766e', marginTop: 4, fontWeight: 600 }}>
                          Already onboarded{row.driver_id ? ` · D-${row.driver_id}` : ''}
                        </div>
                      ) : row.remarks ? (
                        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{row.remarks}</div>
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      {Array.isArray(row.driver_vehicles) && row.driver_vehicles.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {row.driver_vehicles.map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              title="View vehicle"
                              onClick={(e) => {
                                e.stopPropagation();
                                openOnboardedVehicle(v.id);
                              }}
                              style={{
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: '#0f766e',
                                textDecoration: 'underline',
                                fontSize: 13,
                              }}
                            >
                              {`V-${v.id}`}
                              {v.registration_no ? ` · ${v.registration_no}` : ''}
                              {v.body_type || v.vehicle_class ? ` · ${v.body_type || v.vehicle_class}` : ''}
                              {v.is_active === false ? ' (inactive)' : ''}
                            </button>
                          ))}
                          {row.vehicle_type ? (
                            <div style={{ color: '#64748b', fontSize: 12 }}>Asked: {row.vehicle_type}</div>
                          ) : null}
                        </div>
                      ) : row.vehicle_type ? (
                        <div style={{ fontWeight: 600 }}>{row.vehicle_type}</div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={tdStyle}>{formatWhen(row.contact_by || row.follow_up_at)}</td>
                    <td style={tdStyle}>{formatWhen(row.created_at)}</td>
                    <td style={tdStyle}>
                      {row.onboarded_vehicle_id ? (
                        <button
                          type="button"
                          title="View onboarded vehicle"
                          style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                            fontSize: 12, fontWeight: 600, background: badge.background, color: badge.color,
                            border: 'none', cursor: 'pointer', textDecoration: 'underline',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openOnboardedVehicle(row.onboarded_vehicle_id);
                          }}
                        >
                          {`Onboarded (V-${row.onboarded_vehicle_id})`}
                        </button>
                      ) : (
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                          fontSize: 12, fontWeight: 600, background: badge.background, color: badge.color,
                        }}>
                          {status}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        <button type="button" disabled={busy} style={actionBtn('#2563eb')}
                          onClick={() => patchLead(row.id, { status: 'contacted' })}>
                          Contacted
                        </button>
                        <button type="button" disabled={busy} style={actionBtn('#7c3aed')}
                          onClick={() => patchLead(row.id, {
                            status: 'follow_up',
                            follow_up_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
                          })}>
                          Follow up
                        </button>
                        <div style={{ position: 'relative' }}>
                          {row.onboarded_vehicle_id ? (
                            <button
                              type="button"
                              title={`View vehicle V-${row.onboarded_vehicle_id}`}
                              style={actionBtn('#0f766e')}
                              onClick={() => openOnboardedVehicle(row.onboarded_vehicle_id)}
                            >
                              {`View V-${row.onboarded_vehicle_id}`}
                            </button>
                          ) : (row.already_onboarded || row.driver_id) ? (
                            Array.isArray(row.driver_vehicles) && row.driver_vehicles.length > 0 ? (
                              <>
                                <button
                                  type="button"
                                  title="Choose a vehicle to view"
                                  style={actionBtn('#0f766e')}
                                  onClick={() => {
                                    setOnboardMenuLeadId(null);
                                    setVehicleMenuLeadId(
                                      vehicleMenuLeadId === row.id ? null : row.id,
                                    );
                                  }}
                                >
                                  {`View vehicles (${row.driver_vehicles.length})`}
                                </button>
                                {vehicleMenuLeadId === row.id ? (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      top: '110%',
                                      zIndex: 40,
                                      background: '#fff',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 8,
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                      minWidth: 260,
                                      overflow: 'hidden',
                                    }}
                                  >
                                    {row.driver_vehicles.map((v, idx) => (
                                      <button
                                        key={v.id}
                                        type="button"
                                        style={{
                                          ...dropdownItemStyle,
                                          borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                                        }}
                                        onClick={() => {
                                          setVehicleMenuLeadId(null);
                                          openOnboardedVehicle(v.id);
                                        }}
                                      >
                                        {`V-${v.id}`}
                                        {v.registration_no ? ` · ${v.registration_no}` : ''}
                                        {v.body_type || v.vehicle_class
                                          ? ` · ${v.body_type || v.vehicle_class}`
                                          : ''}
                                        {v.is_active === false ? ' (inactive)' : ''}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
                                Onboarded driver
                              </span>
                            )
                          ) : (
                            <>
                              <button
                                type="button"
                                disabled={busy}
                                style={actionBtn('#059669')}
                                onClick={() => setOnboardMenuLeadId(
                                  onboardMenuLeadId === row.id ? null : row.id,
                                )}
                              >
                                Onboard vehicle
                              </button>
                              {onboardMenuLeadId === row.id ? (
                                <div
                                  style={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '110%',
                                    zIndex: 40,
                                    background: '#fff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 8,
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                    minWidth: 240,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <button
                                    type="button"
                                    style={dropdownItemStyle}
                                    onClick={() => goOnboard(row, 'normal')}
                                  >
                                    Onboard Normal Vehicle
                                  </button>
                                  <button
                                    type="button"
                                    style={{ ...dropdownItemStyle, borderTop: '1px solid #f3f4f6' }}
                                    onClick={() => goOnboard(row, 'transporter')}
                                  >
                                    Onboard Transporter Vehicle
                                  </button>
                                </div>
                              ) : null}
                            </>
                          )}
                        </div>
                        <button type="button" disabled={busy} style={actionBtn('#dc2626')}
                          onClick={() => {
                            const reason = window.prompt('Drop reason (optional)') || '';
                            patchLead(row.id, { status: 'dropped', dropped_reason: reason });
                          }}>
                          Drop
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '0 14px 16px', background: '#f8fafc' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginTop: 8 }}>
                          <div style={panelStyle}>
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>Conversation</div>
                            {messages.length === 0 ? (
                              <div style={{ color: '#64748b', fontSize: 13 }}>No messages yet.</div>
                            ) : messages.map((msg, idx) => {
                              const sender = String(msg.sender || 'unknown').toLowerCase();
                              const isDriver = sender === 'driver' || sender === 'user';
                              return (
                                <div key={`${row.id}-m-${idx}`} style={{
                                  display: 'flex',
                                  justifyContent: isDriver ? 'flex-start' : 'flex-end',
                                  marginBottom: 8,
                                }}>
                                  <div style={{
                                    maxWidth: '80%', padding: '8px 10px', borderRadius: 8,
                                    background: isDriver ? '#e2e8f0' : '#dbeafe', fontSize: 13,
                                  }}>
                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>
                                      {sender} · {formatWhen(msg.timestamp)}
                                    </div>
                                    <LeadMessageBody content={msg.content || msg.text} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div style={panelStyle}>
                            <LinkedCallLogs entityType="vehicle_lead" entityId={row.id} />
                            <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>Ops notes</div>
                            {notes.length === 0 ? (
                              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 8 }}>No notes yet.</div>
                            ) : notes.map((n, idx) => (
                              <div key={`${row.id}-n-${idx}`} style={{
                                borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 8, fontSize: 13,
                              }}>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {n.author || 'ops'} · {formatWhen(n.timestamp)}
                                </div>
                                <div>{n.content}</div>
                              </div>
                            ))}
                            <textarea
                              rows={3}
                              value={noteDraft[row.id] || ''}
                              onChange={(e) => setNoteDraft((prev) => ({ ...prev, [row.id]: e.target.value }))}
                              placeholder="Add a note..."
                              style={{ width: '100%', borderRadius: 8, border: '1px solid #cbd5e1', padding: 8 }}
                            />
                            <button
                              type="button"
                              disabled={busy || !(noteDraft[row.id] || '').trim()}
                              style={{ ...pagerBtnStyle, marginTop: 8 }}
                              onClick={async () => {
                                const note = (noteDraft[row.id] || '').trim();
                                if (!note) return;
                                const updated = await patchLead(row.id, { note });
                                if (updated) setNoteDraft((prev) => ({ ...prev, [row.id]: '' }));
                              }}
                            >
                              Save note
                            </button>
                            {row.dropped_reason ? (
                              <div style={{ marginTop: 10, fontSize: 12, color: '#991b1b' }}>
                                Dropped: {row.dropped_reason}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
        <div style={{ color: '#64748b', fontSize: 13 }}>
          {total} lead{total === 1 ? '' : 's'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={pagerBtnStyle}>
            Prev
          </button>
          <span style={{ alignSelf: 'center', fontSize: 13 }}>Page {page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} style={pagerBtnStyle}>
            Next
          </button>
        </div>
      </div>

      <VehicleDetailsModal
        isOpen={vehicleModalOpen}
        vehicleId={selectedVehicleId}
        onClose={() => {
          setVehicleModalOpen(false);
          setSelectedVehicleId(null);
        }}
      />
    </div>
  );
};

const thStyle = {
  padding: '12px 14px',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#64748b',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '12px 14px',
  fontSize: 14,
  color: '#0f172a',
  verticalAlign: 'top',
};

const pagerBtnStyle = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
};

const panelStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  background: '#fff',
  maxHeight: 320,
  overflow: 'auto',
  padding: 12,
};

const actionBtn = (color) => ({
  padding: '5px 10px',
  borderRadius: 6,
  border: 'none',
  background: color,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
});

const dropdownItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 14px',
  color: '#111827',
  background: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
};

export default VehicleLeads;
